import React, { useEffect, useRef } from "react";

/**
 * Lightweight canvas particle field — drifting blue/cyan dots with connecting lines on proximity.
 * Renders on top of star-field for the "alive" feel.
 */
export default function ParticleField({ density = 60, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let raf;
    let particles = [];
    let mouseX = -9999, mouseY = -9999;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };

    const init = () => {
      resize();
      const count = Math.min(density, Math.floor((canvas.width * canvas.height) / (40000 * window.devicePixelRatio)));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3 * window.devicePixelRatio,
        vy: (Math.random() - 0.5) * 0.3 * window.devicePixelRatio,
        r: (Math.random() * 1.5 + 0.6) * window.devicePixelRatio,
        hue: Math.random() > 0.6 ? "#00AFFF" : "#FFFFFF",
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        // mouse attraction
        const dx = (mouseX * window.devicePixelRatio) - p.x;
        const dy = (mouseY * window.devicePixelRatio) - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150 * window.devicePixelRatio) {
          p.x += (dx / dist) * 0.4;
          p.y += (dy / dist) * 0.4;
        }

        ctx.beginPath();
        ctx.fillStyle = p.hue;
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.hue;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const ddx = p.x - q.x;
          const ddy = p.y - q.y;
          const dd = Math.sqrt(ddx * ddx + ddy * ddy);
          if (dd < 120 * window.devicePixelRatio) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0,175,255,${0.15 * (1 - dd / (120 * window.devicePixelRatio))})`;
            ctx.lineWidth = 0.6 * window.devicePixelRatio;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };

    const onLeave = () => {
      mouseX = -9999; mouseY = -9999;
    };

    init();
    tick();
    window.addEventListener("resize", init);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", init);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, [density]);

  return <canvas ref={ref} className={`absolute inset-0 w-full h-full ${className}`} />;
}
